import {
  QAppsCountCard,
  QAppVersionsChartCard,
  QNewlySubmittedAppsCard,
  QAppTypeDistributionCard,
  QTopAppVersionsCard,
  QTopAppCategoriesCard,
} from '@kw/quokka-ui';
import { Box } from '@mui/material';
import dayjs from 'dayjs';
import { DashboardAnalyticsDateRangeDays, DashboardAnalyticsMetrics } from './dashboardAnalytics';
import { useDashboardNavigation } from './useDashboardNavigation';
import { formatCategory } from '../../utils/formatCategory';

type AppInventoryProps = {
  dateRangeDays: DashboardAnalyticsDateRangeDays;
  metrics: DashboardAnalyticsMetrics | null | undefined;
};

export const AppInventory = ({ dateRangeDays, metrics }: AppInventoryProps) => {
  const { getApplicationsHref, getAppReportHref } = useDashboardNavigation(dateRangeDays);

  const newVersionsOverTime = (metrics?.newVersionsOverTime ?? []).map(entry => ({
    date: dayjs(entry.date).format('MMM D'),
    newVersions: entry.total,
  }));

  const newlySubmittedApps = (metrics?.newlySubmittedApps ?? []).map(entry => ({
    ...entry,
    reportHref: getAppReportHref(entry.analysisId),
  }));

  const appTypeDistribution = Object.fromEntries(
    (metrics?.appTypeDistribution ?? []).map(item => [item.managementType, item.versionCount]),
  );

  const topAppCategoriesAndroid = (metrics?.topAppCategoriesAndroid ?? []).map(item => ({
    ...item,
    category: formatCategory(item.category),
    originalCategory: item.category,
  }));

  const topAppCategoriesIos = (metrics?.topAppCategoriesIos ?? []).map(item => ({
    ...item,
    category: formatCategory(item.category),
    originalCategory: item.category,
  }));

  const platforms = ['android', 'ios'] as const;

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: 'repeat(6, 1fr)',
        gap: '24px',
        '& .MuiPaper-root': { width: '100%' },
      }}
    >
      {/* Row 1: Summary cards — each takes 1/3 (2 of 6 cols) */}
      <Box sx={{ gridColumn: 'span 2' }}>
        <QAppsCountCard
          data-testid="total-versions-card"
          variant="total"
          versionsCount={metrics?.totalVersionsAll}
          uniqueAppsCount={metrics?.uniqueAppsAll}
          href={getApplicationsHref({ platform: 'all' })}
        />
      </Box>
      {platforms.map(platform => (
        <Box key={platform} sx={{ gridColumn: 'span 2' }}>
          <QAppsCountCard
            data-testid={`${platform}-versions-card`}
            variant={platform}
            versionsCount={platform === 'android' ? metrics?.totalVersionsAndroid : metrics?.totalVersionsIos}
            uniqueAppsCount={platform === 'android' ? metrics?.uniqueAppsAndroid : metrics?.uniqueAppsIos}
            href={getApplicationsHref({ platform })}
          />
        </Box>
      ))}
      {/* Row 2-3: Chart (2/3) + Newly Submitted (1/3, spans 2 rows), then App Type Distribution (2/3) under chart */}
      <Box sx={{ gridColumn: 'span 4' }}>
        <QAppVersionsChartCard data={newVersionsOverTime} />
      </Box>
      <Box sx={{ gridColumn: 'span 2', gridRow: 'span 2' }}>
        <QNewlySubmittedAppsCard entries={newlySubmittedApps} />
      </Box>
      <Box sx={{ gridColumn: 'span 4' }}>
        <QAppTypeDistributionCard
          managed={appTypeDistribution.MANAGED ?? 0}
          requested={appTypeDistribution.REQUESTED ?? 0}
          unmanaged={appTypeDistribution.DISCOVERED ?? 0}
          getCountHref={item =>
            getApplicationsHref({
              appType: item === 'Unmanaged' ? 'DISCOVERED' : item.toUpperCase(),
            })
          }
        />
      </Box>

      {/* Row 4: Top App Versions Android + iOS side-by-side (1/2 each) */}
      {platforms.map(platform => (
        <Box key={platform} sx={{ gridColumn: 'span 3' }}>
          <QTopAppVersionsCard
            platform={platform}
            data={(platform === 'android' ? metrics?.topAppVersionsAndroid : metrics?.topAppVersionsIos) ?? []}
            getVersionCountHref={item =>
              getApplicationsHref({ searchQuery: item.applicationId, applicationId: item.applicationId, platform })
            }
          />
        </Box>
      ))}

      {/* Row 5: Top App Categories Android + iOS side-by-side (1/2 each) */}
      {platforms.map(platform => (
        <Box key={platform} sx={{ gridColumn: 'span 3' }}>
          <QTopAppCategoriesCard
            platform={platform}
            data={platform === 'android' ? topAppCategoriesAndroid : topAppCategoriesIos}
            getVersionCountHref={item => getApplicationsHref({ categories: item.originalCategory, platform })}
          />
        </Box>
      ))}
    </Box>
  );
};
